// @givmo/board — one person's annual answers as a document: the clean copy, and the changes from
// their previous year (CBG-YR2-WEB1's response page, and the person's own copy in WEB2).
//
// ══ BOTH VIEWS ARE THE RECORD'S OWN WORDS, OR DERIVED FROM TWO RECORDS ══════════════════════════
//
// The clean copy is the response record as read back: every question exactly as it was asked
// (`text_as_asked`, with the organization and the period filled in when it was signed), the answer
// given, and how it was signed or recorded. The changes are the backend's comparison of two recorded
// responses, question by question and entry by key; they are never stored, and say so in one quiet
// line, naming both records by their fingerprints. Nothing here re-asks, re-words or judges an answer.

import type { ReactNode } from 'react';
import { Check, Minus } from 'lucide-react';
import type {
  BoardCoiAnswerOut,
  BoardCoiQuestionChangeOut,
  BoardCoiResponseChangesOut,
  BoardCoiResponseOut,
} from '@givmo/api-client';
import { Card, CardBody, Icon, cn, testIdProp } from '@givmo/design-system';
import { Fingerprint } from '../../components/fingerprint';
import { displayDate } from '../../lib/display';
import {
  KIND_ACKNOWLEDGMENT,
  KIND_ENTRIES,
  PART_POLICY,
  answerWord,
  answersByPart,
  changeFields,
  moment,
} from './coi-decode';
import {
  DELETED,
  DependentBlock,
  EntryLine,
  INSERTED,
  PartLabel,
  StatementText,
} from './coi-parts';
import { coiWords as w, factorWords } from './coi-words';

const BODY = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-strong';
const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary';

/** The signature line, in the record's own facts: who, when, how. */
export function signatureSentence(response: BoardCoiResponseOut): string {
  const name = response.respondent.name ?? '';
  if (response.capture.road === 'paper') {
    return w.recordedFromPaper(
      response.capture.recorded_by_display_name ?? null,
      displayDate(response.signed_on),
    );
  }
  return w.signedBy(name, moment(response.recorded_at), factorWords(response.capture.method));
}

/** The signature block: the line, the sentence affirmed (the signed road), the fingerprint. */
export function SignatureBlock({
  response,
  testId,
  sentence,
}: {
  response: BoardCoiResponseOut;
  testId: string;
  /** The person's own copy words it in the second person (d5-signed); the officer's in the third. */
  sentence?: string;
}) {
  const signed = response.capture.road === 'signed';
  const note = response.capture.provenance_note?.trim() ?? '';
  return (
    <div className="flex flex-col gap-10" {...testIdProp(testId)}>
      <p className={BODY} data-testid={`${testId}-line`}>
        {sentence ?? signatureSentence(response)}
      </p>
      {signed && response.capture.intent_statement_text ? (
        <p className={QUIET} data-testid={`${testId}-intent`}>
          “{response.capture.intent_statement_text}”
        </p>
      ) : null}
      {!signed && note.length > 0 ? <p className={QUIET}>{w.paperNoteLine(note)}</p> : null}
      {response.content_hash ? (
        <Fingerprint value={response.content_hash} testId={`${testId}-fingerprint`} />
      ) : null}
    </div>
  );
}

/** A tick, drawn as the record holds it (never an input: the clean copy is read-only). */
function Tick({ ticked }: { ticked: boolean }) {
  return (
    <span
      className={cn(
        'mt-2 flex size-18 shrink-0 items-center justify-center rounded-sm border',
        ticked
          ? 'border-brand-500 bg-brand-500 text-white'
          : 'border-border-strong bg-surface text-text-tertiary',
      )}
      aria-hidden
    >
      <Icon icon={ticked ? Check : Minus} size={12} />
    </span>
  );
}

/** One answer under its question, as the record holds it. */
function AnswerBlock({
  number,
  answer,
  testId,
}: {
  number: number | null;
  answer: BoardCoiAnswerOut;
  testId: string;
}) {
  return (
    <div
      className="flex flex-col gap-6 border-t border-border-subtle pt-12 first:border-t-0 first:pt-0"
      data-testid={testId}
    >
      <p className={QUIET}>
        {number !== null ? (
          <span className="font-w600 tabular-nums text-text-tertiary">{number}. </span>
        ) : null}
        {answer.text_as_asked}
      </p>
      <p className={cn(BODY, 'font-w600')}>{answerWord(answer.value, answer.kind)}</p>
      {(answer.entries ?? []).map((entry) => (
        <EntryLine key={entry.key} entry={entry} />
      ))}
      {answer.details ? <p className={cn(QUIET, 'whitespace-pre-wrap')}>{answer.details}</p> : null}
    </div>
  );
}

/** What the clean copy reads: a recorded response, or the person's answers before they sign. */
export type CleanCopySource = Pick<
  BoardCoiResponseOut,
  'answers' | 'policy' | 'received_after_close' | 'cycle_label'
>;

/** The clean copy: the policy and its statement, every question as asked and its answer. */
export function CleanCopy({ response, testId }: { response: CleanCopySource; testId: string }) {
  const { statement, interests, other } = answersByPart(response);
  const policy = response.policy ?? null;
  return (
    <div className="flex flex-col gap-20" data-testid={testId}>
      {response.received_after_close ? (
        <p className={QUIET}>{w.receivedAfterLabelClosed(response.cycle_label)}</p>
      ) : null}
      {statement !== null ? (
        <section className="flex flex-col gap-10">
          <PartLabel>{w.boardsPolicy}</PartLabel>
          {policy !== null ? (
            <p className={QUIET}>
              {w.policyAdopted(
                policy.title,
                policy.adopted_on ? displayDate(policy.adopted_on) : null,
              )}
            </p>
          ) : null}
          <div className="flex items-start gap-10" data-testid={`${testId}-statement`}>
            <Tick ticked={statement.value === true} />
            <span className="flex min-w-0 flex-col gap-4">
              <StatementText
                lead={statement.text_as_asked}
                points={statement.points_as_asked ?? []}
                className={BODY}
              />
              {statement.value === true ? null : <span className={QUIET}>{w.notTicked}</span>}
            </span>
          </div>
        </section>
      ) : null}

      {interests.length > 0 ? (
        <section className="flex flex-col gap-14">
          <PartLabel>{w.interests}</PartLabel>
          {interests.map((answer, index) => (
            <AnswerBlock
              key={answer.question_id}
              number={index + 1}
              answer={answer}
              testId={`${testId}-${answer.question_id}`}
            />
          ))}
        </section>
      ) : null}

      {other.map((answer) => (
        <section key={answer.question_id} className="flex flex-col gap-8">
          <PartLabel>{w.anythingElse}</PartLabel>
          <p
            className={typeof answer.value === 'string' && answer.value.trim() ? BODY : QUIET}
            data-testid={`${testId}-${answer.question_id}`}
          >
            {typeof answer.value === 'string' && answer.value.trim()
              ? answer.value
              : w.nothingAdded}
          </p>
        </section>
      ))}
    </div>
  );
}

/** The redline's two answer words, struck and underlined; one plain word when only entries moved. */
function AnswerChange({ question }: { question: BoardCoiQuestionChangeOut }) {
  const before = answerWord(question.previous_value, question.kind);
  const after = answerWord(question.value, question.kind);
  return (
    <p className={cn(BODY, 'flex flex-wrap items-baseline gap-8')}>
      <span className="font-w400 text-text-secondary">{w.answerLabel}</span>
      {before === after ? (
        <span className="font-w600">{after}</span>
      ) : (
        <>
          <span className={cn('font-w600', DELETED)}>{before}</span>
          <span className={cn('font-w600', INSERTED)}>{after}</span>
        </>
      )}
    </p>
  );
}

/** What moved inside one changed answer: removed entries and words, then added ones. */
export function ChangeBlocks({
  question,
  testId,
  removedAside,
}: {
  question: BoardCoiQuestionChangeOut;
  testId: string;
  removedAside?: ReactNode;
}) {
  const removed: ReactNode[] = [];
  const added: ReactNode[] = [];
  for (const entry of question.entries ?? []) {
    if (entry.change === 'removed' || entry.change === 'edited') {
      removed.push(
        <EntryLine key={`r-${entry.key}`} entry={changeFields(entry.before)} mark="del" />,
      );
    }
    if (entry.change === 'added' || entry.change === 'edited') {
      added.push(<EntryLine key={`a-${entry.key}`} entry={changeFields(entry.now)} mark="ins" />);
    }
  }
  const beforeDetails = question.previous_details?.trim() ?? '';
  const afterDetails = question.details?.trim() ?? '';
  if (beforeDetails !== afterDetails) {
    if (beforeDetails.length > 0)
      removed.push(
        <span key="r-details" className={cn(QUIET, DELETED)}>
          {beforeDetails}
        </span>,
      );
    if (afterDetails.length > 0)
      added.push(
        <span key="a-details" className={cn(QUIET, INSERTED)}>
          {afterDetails}
        </span>,
      );
  }
  return (
    <>
      {removed.length > 0 ? (
        <DependentBlock label={w.removed} aside={removedAside} testId={`${testId}-removed`}>
          {removed}
        </DependentBlock>
      ) : null}
      {added.length > 0 ? (
        <DependentBlock label={w.added} testId={`${testId}-added`}>
          {added}
        </DependentBlock>
      ) : null}
    </>
  );
}

/** The statement's line in "No change" (o7-changes), worded by both years' ticks. */
function statementLine(question: BoardCoiQuestionChangeOut): string {
  const now = question.value === true;
  const before = question.previous_value === true;
  if (now && before) return w.statementAgain;
  if (now) return w.statementNow;
  if (before) return w.statementNotNow;
  return w.statementNeither;
}

/**
 * The redline: question by question, what changed from the response this one replaced. The numbers
 * are the questions' places in this year's list, the statement unnumbered.
 */
export function Redline({
  changes,
  previousLabel,
  shortTitleOf,
  testId,
}: {
  changes: BoardCoiResponseChangesOut;
  previousLabel: string;
  /** "Transactions with Harborlight Youth Arts": a question's short title, as the questionnaire names it. */
  shortTitleOf: (questionId: string) => string | null;
  testId: string;
}) {
  const questions = changes.questions ?? [];
  const statement = questions.find((question) => question.kind === KIND_ACKNOWLEDGMENT) ?? null;
  const numbered = questions.filter(
    (question) => question.kind !== KIND_ACKNOWLEDGMENT && question.status !== 'not_asked',
  );
  const numberOf = (question: BoardCoiQuestionChangeOut) => numbered.indexOf(question) + 1;
  const moved = questions.filter(
    (question) =>
      question.kind !== KIND_ACKNOWLEDGMENT &&
      (question.status === 'changed' ||
        question.status === 'new' ||
        question.status === 'not_asked'),
  );
  const unchanged = numbered.filter((question) => question.status === 'unchanged');

  return (
    <div className="flex flex-col gap-20" data-testid={testId}>
      {moved.length > 0 ? (
        <section className="flex flex-col gap-14">
          <PartLabel>{w.changed}</PartLabel>
          {moved.map((question) => (
            <div
              key={question.question_id}
              className="flex flex-col gap-8 border-t border-border-subtle pt-12 first:border-t-0 first:pt-0"
              data-testid={`${testId}-${question.question_id}`}
            >
              <p className={QUIET}>
                {question.status === 'not_asked' ? null : (
                  <span className="font-w600 tabular-nums text-text-tertiary">
                    {numberOf(question)}.{' '}
                  </span>
                )}
                {question.text_as_asked}
              </p>
              {question.status === 'new' ? (
                <>
                  <p className={QUIET}>{w.newThisYear}</p>
                  <p className={cn(BODY, 'font-w600')}>
                    {answerWord(question.value, question.kind)}
                  </p>
                </>
              ) : question.status === 'not_asked' ? (
                <p className={QUIET}>{w.notAskedThisYear}</p>
              ) : (
                <>
                  {question.kind === KIND_ENTRIES || question.previous_value !== question.value ? (
                    <AnswerChange question={question} />
                  ) : null}
                  <ChangeBlocks question={question} testId={`${testId}-${question.question_id}`} />
                </>
              )}
            </div>
          ))}
        </section>
      ) : null}

      <section className="flex flex-col gap-8">
        <PartLabel>{w.noChange}</PartLabel>
        <ul className="flex flex-col gap-6">
          {unchanged.map((question) => (
            <li key={question.question_id} className={QUIET}>
              <span className="font-w600 tabular-nums text-text-tertiary">
                {numberOf(question)}.{' '}
              </span>
              {w.asIn(
                shortTitleOf(question.question_id) ??
                  question.text_as_asked ??
                  question.question_id,
                answerWord(question.value, question.kind).replace(/\.$/, ''),
                previousLabel,
              )}
            </li>
          ))}
          {statement !== null ? <li className={QUIET}>{statementLine(statement)}</li> : null}
        </ul>
      </section>
    </div>
  );
}

/** The two records' fingerprints under the redline (o7-changes). */
export function TwoFingerprints({ changes }: { changes: BoardCoiResponseChangesOut }) {
  const previous = changes.previous ?? null;
  return (
    <div className="flex flex-col gap-8 border-t border-border-subtle pt-16">
      <span className="font-sans text-s12 font-w600 tracking-givmo text-text-tertiary">
        {w.twoFingerprints}
      </span>
      {previous !== null ? (
        <>
          <span className="font-sans text-s12 font-w400 tracking-givmo text-text-secondary">
            {previous.cycle_label}
          </span>
          <code className="break-all font-mono text-s12 text-text-secondary">
            {previous.content_hash}
          </code>
        </>
      ) : null}
      <span className="font-sans text-s12 font-w400 tracking-givmo text-text-secondary">
        {changes.current.cycle_label}
      </span>
      <code className="break-all font-mono text-s12 text-text-secondary">
        {changes.current.content_hash}
      </code>
    </div>
  );
}

/** The document's frame: the header, the derived-copy note, and the body. */
export function DocumentCard({
  header,
  note,
  children,
  testId,
}: {
  header: ReactNode;
  note: ReactNode;
  children: ReactNode;
  testId: string;
}) {
  return (
    <Card testId={testId}>
      <CardBody className="flex flex-col gap-20 p-24 max-sm:p-16">
        {header}
        <p className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
          {note}
        </p>
        {children}
      </CardBody>
    </Card>
  );
}

/** Whether an answer's part is the policy statement (it is never numbered). */
export function isStatement(answer: { part?: string | null }): boolean {
  return answer.part === PART_POLICY;
}
